import { InputAdornment } from '@mui/material';
import { useRef, useState, ChangeEvent, useEffect } from 'react';
import { clsx } from 'clsx';
import { TextField, TextFieldProps } from '../TextField/TextField';
import { useDontMountAtFirst } from '../../shared/hooks/useDontMountAtFirst';
import { useMemoizedDataIds } from '../../shared/utils/dataId';
import { Tooltip } from '../../tooltip/Tooltip';
import { FileUploadInProgressButton } from './components/FileUploadInProgressButton';
import { ClearFileSelectionButton } from './components/ClearFileSelectionButton';
import { AttachFileButton } from './components/AttachFileButton';
import { getDefaultFileData, getFileName, getFileNames } from './utils/fileInputFieldUtils';
import { readFileAsText, validateFileList } from '../../shared/utils/fileReader';
import { FileData } from '../../shared/interfaces/file';
import styles from './FileInputField.module.scss';
import { FILE_INPUT_DATA_IDS } from './fileInputDataId';

export type FileInputProps = {
  onChangeEventHandler: (data: FileData[] | FileData | null) => void;
  value: FileData[] | FileData | null;
  onBlur?: VoidFunction;
  displayRenderFunction?: (names: string[]) => string;
  multiple?: boolean;
  accept?: string;
} & TextFieldProps;

export function FileInputField({
  dataId,
  value,
  label,
  placeholder,
  id,
  onBlur,
  displayRenderFunction = getFileName,
  onChangeEventHandler,
  accept,
  multiple = false,
  ...props
}: FileInputProps) {
  const [fileLoading, setFileLoading] = useState(false);
  const [fileNames, setFileNames] = useState<string[]>(() => getFileNames(value));
  const [fileData, setFileData] = useState<FileData[]>(() => getDefaultFileData(value));

  const inputRef = useRef<HTMLInputElement>(null!);
  const inputFileRef = useRef<HTMLInputElement>(null!);

  const showClearButton = !fileLoading && !!fileData.length;
  const tooltipTitle = fileNames.length > 1 ? fileNames.join(', ') : '';

  useEffect(() => {
    if (props.disabled && value) {
      setFileNames(['************']);
    }
  }, []);

  useDontMountAtFirst(() => {
    if (!multiple) {
      if (fileData.length === 0) {
        onChangeEventHandler(null);
      } else {
        onChangeEventHandler(fileData[0]);
      }
    } else {
      onChangeEventHandler(fileData);
    }
  }, [fileData, id]);

  const handleFileSelector = async (event: ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;

    if (fileList && validateFileList(fileList)) {
      setFileLoading(true);

      const promiseList = Array.from(fileList || []).map(file => readFileAsText(file));
      const names = Array.from(fileList || []).map(file => file.name);

      setFileNames(names);

      Promise.all(promiseList).then(files => {
        if (files.length) {
          const fileContent = files.map((file, index) => ({
            name: fileList[index].name,
            content: file,
            type: fileList[index].type
          }));

          setFileData(fileContent);
        } else {
          setFileData([]);
        }

        setFileLoading(false);
      });
    }
  };

  const triggerFileSelector = () => {
    inputRef.current.blur();
    inputFileRef.current.click();

    if (onBlur) {
      onBlur();
    }
  };

  const resetFileSelector = () => {
    setFileNames([]);
    setFileData([]);

    inputFileRef.current.value = '';

    onChangeEventHandler(null);
  };

  const dataIds = useMemoizedDataIds(FILE_INPUT_DATA_IDS, dataId);

  const displayValue = displayRenderFunction(fileNames);

  return (
    <>
      {tooltipTitle ? (
        <Tooltip dataId={dataIds.title} title={tooltipTitle} placement='bottom-start'>
          <span data-id={dataIds.title} className={styles.shallowTooltip}>
            &nbsp;
          </span>
        </Tooltip>
      ) : null}

      <TextField
        {...props}
        id={id}
        label={!fileNames.length && placeholder ? placeholder : label}
        type='text'
        onBlur={() => {}}
        inputRef={inputRef}
        placeholder={displayValue ? undefined : placeholder}
        autoComplete='off'
        className={clsx(props.className)}
        dataId={dataIds.textField}
        value={displayValue}
        InputProps={{
          readOnly: true,
          onClick: triggerFileSelector,
          tabIndex: -1,
          endAdornment: !props.disabled ? (
            <InputAdornment position='end' data-id={dataIds.endAdornment}>
              {fileLoading ? <FileUploadInProgressButton dataId={dataIds.progress} /> : null}

              {showClearButton ? (
                <ClearFileSelectionButton
                  disabled={props.disabled}
                  resetFileSelector={resetFileSelector}
                  dataId={dataIds.clearBtn}
                />
              ) : null}

              {!fileLoading ? (
                <AttachFileButton
                  disabled={props.disabled}
                  triggerFileSelector={triggerFileSelector}
                  dataId={dataIds.attach}
                />
              ) : null}
            </InputAdornment>
          ) : null
        }}
      />

      <input
        ref={inputFileRef}
        onChange={handleFileSelector}
        name={id}
        disabled={props.disabled}
        type='file'
        hidden
        accept={accept}
        multiple={multiple}
        data-id={dataIds.hiddenInput}
      />
    </>
  );
}
